// Call detail, "What happened" > "Routing": how an inbound call was routed, from the server's
// append-only decisions. Same provenance tone as the timeline; never says who hung up.
import type { Call } from '@bcio-voice/contracts';
import { Fragment, type KeyboardEvent, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { AudioLines, BellOff, CircleAlert, CircleCheck, Grid3x3, Hourglass, ListChecks, PhoneForwarded, PhoneIncoming, PhoneMissed, PhoneOff, Repeat, Server, Timer, Undo2, Users, Voicemail } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import { SkeletonRows } from '../../ui/StatePanel.tsx';
import { useWorkspace } from '../../state/workspace.tsx';
import { useSensitiveResource } from '../../state/useSensitiveResource.ts';
import { hasPermission } from '../../lib/capabilities.ts';
import { clockTime, moveFocus } from '../timeline/timeline.ts';
import {
  DECISION_PERMISSIONS, type DecisionEntry, type DecisionItem, type DecisionLog, type DecisionNames, type DecisionsView, type FinalOutcome, REASON_SENTENCE, ROUTING_PROVENANCE,
  STEP_OUTCOME_LABEL, VOICEMAIL_FINAL_NOTE, decisionItems, decisionsView, entrySentence, finalHeadline, stepDuration, stepSentence,
} from './decisions.ts';
import { FLOW_KIND_LABEL, type FlowEntry, flowEntrySentence } from '../flow/entries.ts';

const FINAL_ICON: Record<FinalOutcome, LucideIcon> = { answered_by_user: PhoneIncoming, missed: PhoneMissed, busy: PhoneOff, unavailable: BellOff, voicemail: Voicemail };
const KIND_ICON: Partial<Record<DecisionEntry['kind'], LucideIcon>> = { rule_matched: ListChecks, legacy_route: ListChecks, redirected: PhoneForwarded, fallback: Undo2, final: CircleCheck };
const FLOW_ICON: Record<FlowEntry['kind'], LucideIcon> = {
  queue_entered: Hourglass, queue_rering: Repeat, queue_answered: PhoneIncoming, queue_overflow: Undo2, queue_abandoned: PhoneOff,
  ivr_prompted: AudioLines, ivr_choice: Grid3x3, ivr_timeout: Timer, ivr_invalid: CircleAlert, ivr_default: Undo2,
};

export function RoutingDecisionsSection({ call }: { call: Call }) {
  const ws = useWorkspace();
  const canRead = DECISION_PERMISSIONS.some(p => hasPermission(ws.permissions, p));
  // Reload when the call moves on (a live call's routing finishes as it is answered or ends).
  const refreshKey = `${call.state}|${call.outcome ?? ''}|${call.answered_at ?? ''}`;
  const resource = useSensitiveResource<{ data?: unknown }>(canRead ? `/routing/decisions?call_id=${encodeURIComponent(call.id)}` : null, refreshKey);
  const view = decisionsView({ canRead, status: resource.status, value: resource.value, error: resource.error });
  return (
    <section className="detail-section rd-section" aria-labelledby="rd-title">
      <h4 id="rd-title" className="subsection-title">Routing</h4>
      <RoutingDecisionsBody view={view} onRetry={resource.reload} />
    </section>
  );
}

/** The panel body for one decided state (exported for tests). */
export function RoutingDecisionsBody({ view, onRetry }: { view: DecisionsView; onRetry?: () => void }) {
  const ws = useWorkspace();
  const names: DecisionNames = {
    person: userId => ws.userName(userId),
    number: numberId => ws.numbers.value?.find(n => n.id === numberId) ?? null,
  };
  switch (view.state) {
    case 'denied':
      return <Note text="Your role can’t view how calls were routed." code={view.code ?? 'permission_denied'} requestId={view.requestId} />;
    case 'none':
      return <Note text="No routing decisions are available for this call. It may predate routing rules, or it isn’t shared with you." code="not_found" requestId={view.requestId} />;
    case 'loading':
      return <SkeletonRows rows={2} label="Loading routing decisions" />;
    case 'access':
      return view.access === 'refreshing' ? <p className="field-hint" role="status">Your access changed. Checking what you can see…</p> : <p className="inline-reason" role="status">Sign in again to see how this call was routed.</p>;
    case 'error':
      return (
        <div className="notice-end" role="alert">
          <div>
            <p>{view.error.code === 'network_error' ? view.error.title : 'Couldn’t load routing decisions.'}</p>
            {onRetry ? <p><button type="button" className="btn btn-small" onClick={onRetry}>Try again</button></p> : null}
            <Reference code={view.error.code} requestId={view.error.requestId} />
          </div>
        </div>
      );
    case 'empty':
      return <p className="field-hint">No routing decisions were recorded for this call. Numbers without an enabled routing rule, and calls from before routing rules, have none.</p>;
    case 'ready':
      return <Ready log={view.log} names={names} />;
  }
}

function Note({ text, code, requestId }: { text: string; code: string | null; requestId: string | null }) {
  return (
    <div className="field-hint rd-note">
      <p>{text}</p>
      <Reference code={code} requestId={requestId} />
    </div>
  );
}

function Reference({ code, requestId }: { code: string | null; requestId: string | null }) {
  if (!code && !requestId) return null;
  return (
    <details className="diagnostics">
      <summary>Details</summary>
      <dl>
        {code ? <><dt>Code</dt><dd className="mono">{code}</dd></> : null}
        {requestId ? <><dt>Reference</dt><dd className="mono">{requestId}</dd></> : null}
      </dl>
    </details>
  );
}

function Ready({ log, names }: { log: DecisionLog; names: DecisionNames }) {
  const ws = useWorkspace();
  const tz = ws.config.value?.tenant_timezone;
  const items = decisionItems(log.entries, log.flow);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLOListElement>(null);
  const focusIndex = Math.min(active, items.length - 1);
  const final = log.entries.find(e => e.kind === 'final');
  const finalReason = final?.reason ? REASON_SENTENCE[final.reason] : undefined;

  const onKeyDown = (e: KeyboardEvent<HTMLOListElement>) => {
    const nodes = [...(listRef.current?.querySelectorAll<HTMLElement>('[data-rd-item]') ?? [])];
    const current = nodes.findIndex(node => node === e.target);
    if (current < 0) return;
    const next = moveFocus(e.key, current, nodes.length);
    if (next === null) return;
    e.preventDefault();
    setActive(next);
    nodes[next]?.focus();
  };

  return (
    <>
      <div className={`wh-summary${log.finalOutcome === 'voicemail' ? ' rd-voicemail' : ''}`}>
        <p className="wh-headline">
          <Icon icon={log.finalOutcome ? FINAL_ICON[log.finalOutcome] : Hourglass} /> {finalHeadline(log.finalOutcome, log.answeredBy, names)}
        </p>
        {finalReason && log.finalOutcome !== 'answered_by_user' ? <p>{finalReason}</p> : null}
        {log.finalOutcome === 'voicemail' ? <p>{VOICEMAIL_FINAL_NOTE}</p> : null}
        <p className="meta wh-provenance">{ROUTING_PROVENANCE}</p>
      </div>
      {items.length ? (
        <>
          <p className="meta" id="rd-hint">Decisions in the order the server made them. Use the arrow keys to move between them.</p>
          <ol ref={listRef} className="tl-list rd-list" aria-labelledby="rd-title" aria-describedby="rd-hint" onKeyDown={onKeyDown}>
            {items.map((item, index) => (
              <DecisionRow key={item.kind === 'step' ? `step-${item.step}` : item.entry.id} item={item} names={names} tz={tz} focusable={index === focusIndex} onFocus={() => setActive(index)} />
            ))}
          </ol>
        </>
      ) : null}
      {log.skipped > 0 ? <p className="meta">{log.skipped} routing decision{log.skipped === 1 ? '' : 's'} couldn’t be shown in this version.</p> : null}
    </>
  );
}

function DecisionRow({ item, names, tz, focusable, onFocus }: { item: DecisionItem; names: DecisionNames; tz: string | undefined; focusable: boolean; onFocus: () => void }) {
  const first = item.kind === 'step' ? item.started ?? item.ended : item.entry;
  const at = first?.at ?? '';
  const common = { 'data-rd-item': '', tabIndex: focusable ? 0 : -1, onFocus };
  if (item.kind === 'flow') {
    const f = item.entry;
    return (
      <li className="tl-item rd-item rd-flow" {...common}>
        <span className="tl-when"><time dateTime={f.at}>{clockTime(f.at, tz)}</time></span>
        <span className="tl-source source-controller"><Icon icon={FLOW_ICON[f.kind]} /> {FLOW_KIND_LABEL[f.kind]}</span>
        <span className="tl-text">{flowEntrySentence(f, names)}</span>
        <details className="diagnostics tl-details">
          <summary>Technical details</summary>
          <dl>
            <dt>Decision</dt><dd className="mono">{f.kind} #{f.sequence}</dd>
            {f.ring_group_id ? <><dt>Ring group</dt><dd className="mono">{f.ring_group_id}</dd></> : null}
            {f.menu_id ? <><dt>Menu</dt><dd className="mono">{f.menu_id}{f.menu_version ? ` v${f.menu_version}` : ''}</dd></> : null}
            {f.attempt !== null ? <><dt>Attempt</dt><dd className="mono">{f.attempt}</dd></> : null}
            {f.wait_ms !== null ? <><dt>Wait</dt><dd className="mono">{f.wait_ms} ms</dd></> : null}
            {f.target ? <><dt>Target</dt><dd className="mono">{f.target.kind}{f.target.id ? ` ${f.target.id}` : ''}</dd></> : null}
            {f.reason ? <><dt>Reason</dt><dd className="mono">{f.reason}</dd></> : null}
            <dt>Source</dt><dd>Routing controller (server)</dd>
          </dl>
        </details>
      </li>
    );
  }
  if (item.kind === 'step') {
    const { started, ended } = item;
    const startedAt = started?.started_at ?? ended?.started_at ?? started?.at ?? null;
    const endedAt = ended?.ended_at ?? (ended ? ended.at : null);
    const duration = stepDuration(startedAt, endedAt);
    return (
      <li className="tl-item rd-item" {...common}>
        <span className="tl-when"><time dateTime={at}>{clockTime(at, tz)}</time></span>
        <span className="tl-source source-controller"><Icon icon={Users} /> Ring step</span>
        <span className="tl-text">
          {stepSentence(started, ended, item.step, names)}{' '}
          {ended?.outcome ? <strong>{STEP_OUTCOME_LABEL[ended.outcome]}.</strong> : <span className="meta">No end was recorded for this step yet.</span>}
          {duration ? <span className="meta"> Rang for {duration}{endedAt ? <>, until <time dateTime={endedAt}>{clockTime(endedAt, tz)}</time></> : null}.</span> : null}
        </span>
        <Technical entries={[started, ended].filter((e): e is DecisionEntry => e !== null)} />
      </li>
    );
  }
  const e = item.entry;
  return (
    <li className={`tl-item rd-item${e.kind === 'final' ? ' is-terminal' : ''}`} {...common}>
      <span className="tl-when"><time dateTime={e.at}>{clockTime(e.at, tz)}</time></span>
      <span className="tl-source source-controller"><Icon icon={KIND_ICON[e.kind] ?? Server} /> Routing</span>
      <span className="tl-text">
        {entrySentence(e, names)}
        {(e.kind === 'rule_matched' || e.kind === 'legacy_route') && e.reason && REASON_SENTENCE[e.reason] ? <span className="meta"> {REASON_SENTENCE[e.reason]}</span> : null}
      </span>
      <Technical entries={[e]} />
    </li>
  );
}

function Technical({ entries }: { entries: DecisionEntry[] }) {
  return (
    <details className="diagnostics tl-details">
      <summary>Technical details</summary>
      <dl>
        {entries.map(e => (
          <Fragment key={e.id}>
            <dt>Decision</dt><dd className="mono">{e.kind} #{e.sequence}</dd>
            {e.rule_id ? <><dt>Rule</dt><dd className="mono">{e.rule_id}{e.rule_version ? ` v${e.rule_version}` : ''}</dd></> : null}
            {e.target ? <><dt>Target</dt><dd className="mono">{e.target.kind}{e.target.id ? ` ${e.target.id}` : ''}</dd></> : null}
            {e.outcome ? <><dt>Outcome</dt><dd className="mono">{e.outcome}</dd></> : null}
            {e.reason ? <><dt>Reason</dt><dd className="mono">{e.reason}</dd></> : null}
            {e.leg_ids.length ? <><dt>Legs</dt><dd className="mono">{e.leg_ids.length}</dd></> : null}
          </Fragment>
        ))}
        <dt>Source</dt><dd>Routing controller (server)</dd>
      </dl>
    </details>
  );
}
